import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import { GenericModalSkeleton } from '../Skeletons/ModalSkeletons';

interface GenericModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  loading?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  height?: string;
  showBreadcrumb?: boolean;
  breadcrumbItems?: Array<{
    label: string;
    onClick?: () => void;
  }>;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const GenericModal: React.FC<GenericModalProps> = ({
  open,
  onClose,
  title,
  loading = false,
  maxWidth = 'xl',
  fullWidth = true,
  height = '90vh',
  showBreadcrumb = false,
  breadcrumbItems = [],
  actions,
  children
}) => {
  const renderBreadcrumb = () => {
    if (!showBreadcrumb || breadcrumbItems.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs separator="›">
          <Link
            component="button"
            variant="body2"
            onClick={onClose}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Voltar
          </Link>
          {breadcrumbItems.map((item, index) => (
            <Typography
              key={index}
              variant="body2"
              color={index === breadcrumbItems.length - 1 ? "text.primary" : "text.secondary"}
              component={item.onClick ? "button" : "span"}
              onClick={item.onClick}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: item.onClick ? 'pointer' : 'default',
                '&:hover': item.onClick ? { textDecoration: 'underline' } : {}
              }}
            >
              {item.label}
            </Typography>
          ))}
        </Breadcrumbs>
      </Box>
    );
  };

  const renderLoadingState = () => (
    <GenericModalSkeleton height={400} />
  );

  const renderContent = () => {
    if (loading) {
      return renderLoadingState();
    }

    return (
      <Box>
        {renderBreadcrumb()}
        {children}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          height: height,
          maxHeight: height
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" component="span">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        {renderContent()}
      </DialogContent>
      
      <DialogActions>
        {actions || <Button onClick={onClose}>Fechar</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default GenericModal; 